"use client";

import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { ComponentPropsWithoutRef, FC, useContext } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  useCancelledAccess,
  useOpenResubscribeWall,
} from "../hooks/useCancelledAccess";
import { useIsEmbedded } from "../hooks/useIsEmbedded";
import { ModalContext } from "../hooks/useModal";
import { useProfile } from "../hooks/useProfile";
import Button from "./Button";
import NeedPaidPlan from "./NeedPaidPlan";
import Typography from "./Typography";

type FreeTrialBannerProps = {
  /** Mounted by the `/schedule` workspace inside its own height. */
  inWorkspace?: boolean;
} & ComponentPropsWithoutRef<"div">;

const FreeTrialBanner: FC<FreeTrialBannerProps> = ({
  inWorkspace = false,
  ...rest
}) => {
  const modal = useContext(ModalContext);
  const posthog = usePostHog();
  const { data: profile } = useProfile();
  const [_showFreeTrialBanner] = useLocalStorage("showFreeTrialBanner", true);
  const isEmbedded = useIsEmbedded();
  const pathname = usePathname();
  const { gated: subscriptionEnded } = useCancelledAccess();
  const openResubscribeWall = useOpenResubscribeWall();

  if (pathname?.includes("/editor")) return <></>;
  // `/schedule` fills the viewport (h-dvh): a banner stacked above it makes
  // the page taller than the screen and pushes the workspace's bottom bar
  // under the browser's toolbar, so the workspace mounts its own copy
  // inside that height (schedule-content.tsx) and this one stands aside.
  if (pathname === "/schedule" && !inWorkspace) return <></>;
  if (!profile || profile.plan || profile.isGuest || isEmbedded) return <></>;

  return (
    <div
      {...rest}
      className={`${rest.className} flex justify-center items-center gap-3 dark px-3 py-1 bg-slate-950 text-white border-b border-slate-800 relative z-[45]`}
    >
      <div className=" items-baseline md:gap-2">
        <Typography variant="caption" className="text-white block">
          {subscriptionEnded
            ? "Your subscription has ended. Resubscribe to get back to your clips."
            : "You're in free trial. Some features are limited. Upgrade to unlock all features."}
        </Typography>
      </div>
      <div className="flex gap-1">
        <Button
          size="m"
          variant="primary"
          onClick={() => {
            // A lapsed subscriber the cancelled-user gate covers
            // (helpers/cancelledAccess.ts) isn't in a free trial.
            if (subscriptionEnded) {
              openResubscribeWall("banner");
              return;
            }
            posthog?.capture("Upgrade Button Clicked (free trial banner)");
            posthog?.capture("Paywall Dialog Showed", {
              feature: "all",
              source: "free-trial-banner",
            });
            modal.open(<NeedPaidPlan />, {
              className: "max-w-6xl",
            });
          }}
        >
          {subscriptionEnded ? "Resubscribe" : "Upgrade"}
        </Button>
        {/* <Button
          size="s"
          onClick={handleClickClose}
          leading={<XMarkIcon className="w-4 h-4" />}
        /> */}
      </div>
    </div>
  );
};

export default FreeTrialBanner;
